/**
 * FamilyPage
 * Kitchly v0.5.0
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Users,
  Plus,
  UserPlus,
  Copy,
  Crown,
  User,
  Check,
  X,
  Trash2,
  LogOut,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { groups as groupsApi } from '../services/api';

function FamilyPage() {
  const { t } = useTranslation();
  const { user, groups: userGroups, activeGroupId, switchGroup, refreshUser } = useAuth();

  const [groupDetails, setGroupDetails] = useState(null);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const loadGroupDetails = useCallback(async () => {
    if (!activeGroupId) return;
    try {
      const data = await groupsApi.get(activeGroupId);
      setGroupDetails(data);
    } catch {
      setGroupDetails(null);
    }
  }, [activeGroupId]);

  const loadPendingInvitations = useCallback(async () => {
    try {
      const data = await groupsApi.pendingInvitations();
      setPendingInvitations(data.invitations || []);
    } catch {
      // Silent
    }
  }, []);

  useEffect(() => {
    loadGroupDetails();
    loadPendingInvitations();
  }, [loadGroupDetails, loadPendingInvitations]);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    setLoading(true);

    try {
      const result = await groupsApi.create(newGroupName.trim());
      toast.success(t('family.groupCreated', { name: newGroupName.trim() }));
      switchGroup(result.id);
      setNewGroupName('');
      setShowCreate(false);
      await refreshUser();
      loadGroupDetails();
    } catch (err) {
      toast.error(err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGroup = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    setLoading(true);

    try {
      const result = await groupsApi.join(joinCode.trim());
      toast.success(t('family.groupJoined', { name: result.group_name }));
      switchGroup(result.group_id);
      setJoinCode('');
      setShowJoin(false);
      await refreshUser();
      loadGroupDetails();
    } catch (err) {
      toast.error(err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !activeGroupId) return;
    setLoading(true);

    try {
      await groupsApi.invite(activeGroupId, inviteEmail.trim());
      toast.success(t('family.inviteSent', { email: inviteEmail.trim() }));
      setInviteEmail('');
      setShowInvite(false);
      loadGroupDetails();
    } catch (err) {
      toast.error(err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleRespondInvitation = async (invitationId, action, groupName) => {
    try {
      await groupsApi.respondInvitation(invitationId, action);
      if (action === 'accept') {
        toast.success(t('family.invitationAccepted', { name: groupName }));
        await refreshUser();
      } else {
        toast.success(t('family.invitationDeclined'));
      }
      loadPendingInvitations();
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(t('family.removeMemberConfirm', { name: memberName }))) return;
    try {
      await groupsApi.removeMember(activeGroupId, memberId);
      toast.success(t('family.memberRemoved', { name: memberName }));
      loadGroupDetails();
    } catch (err) {
      toast.error(err.message || t('common.error'));
    }
  };

  const copyInviteCode = () => {
    if (groupDetails?.group?.invite_code) {
      navigator.clipboard.writeText(groupDetails.group.invite_code)
        .then(() => toast.success(t('family.codeCopied')))
        .catch(() => toast.error(t('common.error')));
    }
  };

  const isAdmin = groupDetails?.members?.find(m => m.id === user?.id)?.role === 'admin';

  return (
    <div className="family-page">
      <div className="page-header">
        <h1>{t('family.title')}</h1>
      </div>

      {/* Group Switcher */}
      {userGroups.length > 0 && (
        <div className="card mb-lg">
          <label className="form-label">{t('family.switchGroup')}</label>
          <select
            className="form-select"
            value={activeGroupId || ''}
            onChange={(e) => {
              switchGroup(e.target.value || null);
              if (e.target.value) loadGroupDetails();
            }}
          >
            <option value="">{t('family.personalMode')}</option>
            {userGroups.map(g => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div className="card mb-lg">
          <h3 className="card-title mb-md">{t('family.pendingInvitations')}</h3>
          {pendingInvitations.map(inv => (
            <div key={inv.id} className="list-item">
              <div className="list-item-content">
                <div className="list-item-title">{inv.group_name}</div>
                <div className="list-item-subtitle">
                  {t('family.inviteByEmail')} {inv.invited_by_name}
                </div>
              </div>
              <div className="list-item-actions">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => handleRespondInvitation(inv.id, 'accept', inv.group_name)}
                >
                  <Check size={14} /> {t('family.accept')}
                </button>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => handleRespondInvitation(inv.id, 'decline', inv.group_name)}
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No group state */}
      {!activeGroupId && userGroups.length === 0 && (
        <div className="empty-state">
          <Users size={64} className="empty-state-icon" />
          <h3>{t('family.noGroup')}</h3>
          <p>{t('family.noGroupDescription')}</p>
        </div>
      )}

      {/* Create / Join buttons */}
      {(!activeGroupId || userGroups.length === 0) && (
        <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
          <button className="btn btn-primary btn-full" onClick={() => setShowCreate(true)}>
            <Plus size={18} /> {t('family.createGroup')}
          </button>
          <button className="btn btn-secondary btn-full" onClick={() => setShowJoin(true)}>
            <UserPlus size={18} /> {t('family.joinGroup')}
          </button>
        </div>
      )}

      {/* Create Group Form */}
      {showCreate && (
        <div className="card mb-lg">
          <h3 className="card-title mb-md">{t('family.createGroup')}</h3>
          <form onSubmit={handleCreateGroup}>
            <div className="form-group">
              <label className="form-label">{t('family.groupName')}</label>
              <input
                type="text"
                className="form-input"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Smith Family"
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? t('common.loading') : t('family.create')}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Join Group Form */}
      {showJoin && (
        <div className="card mb-lg">
          <h3 className="card-title mb-md">{t('family.joinGroup')}</h3>
          <form onSubmit={handleJoinGroup}>
            <div className="form-group">
              <label className="form-label">{t('family.inviteCode')}</label>
              <input
                type="text"
                className="form-input"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                placeholder="ABC12345"
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? t('common.loading') : t('family.join')}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowJoin(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Active Group Details */}
      {activeGroupId && groupDetails && (
        <>
          {/* Members */}
          <div className="card mb-lg">
            <div className="card-header">
              <h3 className="card-title">{t('family.members')}</h3>
              {isAdmin && (
                <button className="btn btn-sm btn-secondary" onClick={() => setShowInvite(!showInvite)}>
                  <UserPlus size={16} /> {t('family.sendInvite')}
                </button>
              )}
            </div>

            {/* Invite form */}
            {showInvite && (
              <form onSubmit={handleInvite} style={{ marginBottom: 'var(--space-md)' }}>
                <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                  <input
                    type="email"
                    className="form-input"
                    placeholder={t('family.inviteEmail')}
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {t('family.sendInvite')}
                  </button>
                </div>
              </form>
            )}

            {/* Member list */}
            {groupDetails.members?.map(member => (
              <div key={member.id} className="list-item">
                <div
                  className="list-item-icon"
                  style={{ background: 'var(--color-primary-bg)', color: 'var(--color-primary)', borderRadius: 'var(--radius-full)' }}
                >
                  {member.role === 'admin' ? <Crown size={18} /> : <User size={18} />}
                </div>
                <div className="list-item-content">
                  <div className="list-item-title">
                    {member.display_name}
                    {member.id === user?.id && <span className="text-xs text-secondary"> (you)</span>}
                  </div>
                  <div className="list-item-subtitle">
                    <span className={`badge ${member.role === 'admin' ? 'badge-accent' : 'badge-primary'}`}>
                      {member.role === 'admin' ? t('family.admin') : t('family.member')}
                    </span>
                  </div>
                </div>
                {isAdmin && member.id !== user?.id && (
                  <button
                    className="btn-icon"
                    onClick={() => handleRemoveMember(member.id, member.display_name)}
                    title={t('family.removeMember')}
                  >
                    <Trash2 size={16} color="var(--color-error)" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Invite Code */}
          <div className="card mb-lg">
            <div className="card-header">
              <h3 className="card-title">{t('family.inviteCode')}</h3>
              <button className="btn btn-sm btn-secondary" onClick={copyInviteCode}>
                <Copy size={14} /> {t('family.copyCode')}
              </button>
            </div>
            <div style={{
              background: 'var(--color-bg-input)',
              padding: 'var(--space-md)',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'monospace',
              fontSize: '18px',
              textAlign: 'center',
              letterSpacing: '2px',
              fontWeight: '600'
            }}>
              {groupDetails.group?.invite_code}
            </div>
          </div>
        </>
      )}

      {/* Solo mode indicator */}
      {!activeGroupId && userGroups.length > 0 && (
        <div className="banner banner-info">
          <span>{t('family.soloMode')}</span>
        </div>
      )}
    </div>
  );
}

export default FamilyPage;
